import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Flame,
  Pencil,
  Plus,
  Trash2
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Control, UseFormSetValue, useForm, useWatch } from "react-hook-form";
import { useGetFacilityHeatSources } from "../../../api/facility";
import { useGetHeatSourceType } from "../../../api/static-data";
import { Badge } from "../../../components/ui/common/badge";
import { Button } from "../../../components/ui/common/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "../../../components/ui/common/collapsible";
import { Form } from "../../../components/ui/common/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../components/ui/common/tooltip";
import { FormSelect } from "../../../components/ui/Input/FormSelector/FormSelect";
import { FormUnitInput } from "../../../components/ui/Input/FormUnitInput/FormUnitInput";
import { ENERGY_UNIT_TYPES_OPTIONs, PAGINATION_ITEMS_PER_PAGE } from "../../../config/constants";
import { MEASUREMENT_TYPES } from "../../../config/enums/MeasurementTypes";
import { validateHeatSourceAmount, valueGraterThanZero } from "../../../utils/utils";

interface HeatSourceData {
  HeatSource: number | string;
  HeatAmount: number | string;
  recoveredSource?: string;
  heatUnit?: string | number;
}

interface HeatSourceRecord {
  HeatSource: number;
  HeatAmount: number;
  recoveredSource?: string;
  heatUnit?: string | number;
}

interface ProcessHeatSourceProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  lockStatus?: boolean;
}

const ProcessHeatSource = ({ control, setValue, lockStatus = false }: ProcessHeatSourceProps) => {
  const [heatOpen, setHeatOpen] = useState(true);
  const [heatPage, setHeatPage] = useState(1);
  const [editingHeatId, setEditingHeatId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<(HeatSourceRecord & { index: number }) | null>(
    null
  );
  const initializedRef = useRef(false);

  const facilityName = useWatch({ control, name: "facilityName" });
  const heatSourceData = useWatch({ control, name: "heatSource" });
  const heatValue = useWatch({ control, name: "heat" });
  const heatSource = useMemo(() => heatSourceData || [], [heatSourceData]);

  const { data: availableHeatSourceType } = useGetFacilityHeatSources(facilityName);
  const { data: heatSourceType } = useGetHeatSourceType();

  const defaultHeatSourceType = useMemo(() => {
    return availableHeatSourceType && availableHeatSourceType.length > 0
      ? availableHeatSourceType[0].value
      : "1";
  }, [availableHeatSourceType]);

  // Initialize heatSource array from heat value on first load.
  // Uses a ref to avoid race conditions between availableHeatSourceType and heatValue loading asynchronously.
  useEffect(() => {
    if (initializedRef.current) return;
    if (!availableHeatSourceType || availableHeatSourceType.length === 0) return;
    if (heatValue === undefined || heatValue === null) return;

    initializedRef.current = true;

    const hasHeatValue = Number(heatValue) > 0;
    const hasHeatSource = heatSource && heatSource.length > 0;

    // If heat value exists but no heatSource array yet, create default entry with first available heat source
    if (hasHeatValue && !hasHeatSource) {
      const defaultRecord: HeatSourceRecord = {
        HeatSource: Number(defaultHeatSourceType),
        HeatAmount: Number(heatValue),
        recoveredSource: undefined
      };
      setValue("heatSource", [defaultRecord], { shouldDirty: false, shouldValidate: false });
    }
  }, [heatValue, heatSource, availableHeatSourceType, defaultHeatSourceType, setValue]);

  // Separate form for add/edit operations (matches original pattern)
  const heatForm = useForm<HeatSourceData>({
    mode: "all",
    defaultValues: {
      HeatSource: defaultHeatSourceType,
      HeatAmount: "",
      recoveredSource: "",
      heatUnit: MEASUREMENT_TYPES.ENERGY_MJ_KG
    }
  });

  const selectedHeatSource = heatForm.watch("HeatSource");

  const totalHeat = useMemo(() => {
    if (!heatSource || !Array.isArray(heatSource)) return 0;
    return heatSource.reduce(
      (sum, field: HeatSourceRecord) => sum + (Number(field?.HeatAmount) || 0),
      0
    );
  }, [heatSource]);

  useEffect(() => {
    setValue("heat", totalHeat, { shouldDirty: true, shouldValidate: true });
  }, [totalHeat, setValue]);

  const heatPages = Math.ceil(heatSource.length / PAGINATION_ITEMS_PER_PAGE);
  const paginatedHeat = heatSource.slice(
    (heatPage - 1) * PAGINATION_ITEMS_PER_PAGE,
    heatPage * PAGINATION_ITEMS_PER_PAGE
  );

  const handleHeatFormSubmit = heatForm.handleSubmit((data: HeatSourceData) => {
    if (!data.HeatSource) return;

    const newRecord: HeatSourceRecord = {
      HeatSource: Number(data.HeatSource),
      HeatAmount: Number(data.HeatAmount) || 0
    };

    if (editingHeatId !== null && editingItem) {
      // Update existing heat source (edit mode)
      const updatedHeatSource = heatSource.map((item: any, index: number) =>
        index === editingItem.index ? newRecord : item
      );
      setValue("heatSource", updatedHeatSource, { shouldDirty: true, shouldValidate: true });
      setEditingHeatId(null);
      setEditingItem(null);
    } else {
      // Check if heat source already exists and update, otherwise add (add mode)
      const existingIndex = heatSource.findIndex(
        (item: any) => item.HeatSource === newRecord.HeatSource
      );

      if (existingIndex !== -1) {
        const updatedHeatSource = heatSource.map((item: any, index: number) =>
          index === existingIndex ? { ...item, HeatAmount: newRecord.HeatAmount } : item
        );
        setValue("heatSource", updatedHeatSource, { shouldDirty: true, shouldValidate: true });
      } else {
        setValue("heatSource", [...heatSource, newRecord], {
          shouldDirty: true,
          shouldValidate: true
        });
        setHeatPage(Math.ceil((heatSource.length + 1) / PAGINATION_ITEMS_PER_PAGE));
      }

      // Reset editing state in add mode as well
      setEditingHeatId(null);
      setEditingItem(null);
    }

    // Reset form
    heatForm.reset({
      HeatSource: defaultHeatSourceType,
      HeatAmount: "",
      recoveredSource: "",
      heatUnit: MEASUREMENT_TYPES.ENERGY_MJ_KG
    });
  });

  const handleDeleteHeatSource = (index: number): void => {
    const updatedHeatSource = heatSource.filter((_: HeatSourceRecord, i: number) => i !== index);
    setValue("heatSource", updatedHeatSource, { shouldDirty: true, shouldValidate: true });

    const newLen = updatedHeatSource.length;
    const newPages = Math.ceil(newLen / PAGINATION_ITEMS_PER_PAGE);
    if (heatPage > newPages && newPages > 0) setHeatPage(newPages);
  };

  const renderHeatRow = (
    item: HeatSourceRecord,
    index: number,
    isDraft = false
  ): JSX.Element | null => {
    if (isDraft) {
      return (
        <Form {...heatForm}>
          <form key="draft-row" className="tw-space-y-1" onSubmit={handleHeatFormSubmit}>
            <div className="tw-grid tw-grid-cols-[1fr_250px_auto] tw-gap-2 tw-items-center">
              <FormSelect
                control={heatForm.control}
                name="HeatSource"
                placeholder="Select heat source"
                options={availableHeatSourceType}
                disabled={lockStatus}
                required
                className="!tw-h-10"
              />
              <FormUnitInput
                control={heatForm.control}
                name="HeatAmount"
                unitOptions={ENERGY_UNIT_TYPES_OPTIONs}
                unitName="heatUnit"
                placeholder="0.00"
                enableLimitedDecimal
                viewDecimalNumber={12}
                disabled={lockStatus}
                required="Heat amount is required"
                validate={{
                  valueCheck: (v: string | number) =>
                    valueGraterThanZero(v) || "Heat Amount must be greater than 0",
                  maxCheck: (v: string | number) => validateHeatSourceAmount(v)
                }}
              />
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="submit"
                      variant="default"
                      size="icon"
                      className="tw-w-10 tw-h-10 tw-mt-[5px]"
                      disabled={
                        !selectedHeatSource || lockStatus || !availableHeatSourceType?.length
                      }
                    >
                      <Plus className="tw-h-3.5 tw-w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="tw-text-xs">Update changes</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </form>
        </Form>
      );
    }

    const fieldIndex = index;

    // Get heat source label from heatSourceType
    const heatSourceLabel =
      heatSourceType?.find((s: any) => String(s.value) === String(item?.HeatSource))?.text ||
      item?.HeatSource ||
      "—";

    const itemId = `heat-${item.HeatSource}-${index}`;
    const isReadOnly = editingHeatId !== itemId || lockStatus;

    // Read-only view
    if (isReadOnly) {
      return (
        <div
          key={itemId}
          className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-2 tw-rounded-md tw-bg-muted/30 tw-border tw-border-border/50"
        >
          <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-text-xs">
            <span className="tw-font-medium tw-text-foreground">{heatSourceLabel}</span>
            <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">
              {item?.HeatAmount || 0} MJ/kg
            </span>
          </div>
          {!lockStatus && (
            <>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
                      onClick={() => {
                        // Populate form with item data (edit mode)
                        heatForm.reset({
                          HeatSource: String(item.HeatSource),
                          HeatAmount: String(item.HeatAmount),
                          recoveredSource: item.recoveredSource || "",
                          heatUnit: item.heatUnit || MEASUREMENT_TYPES.ENERGY_MJ_KG
                        });
                        setEditingHeatId(itemId);
                        setEditingItem({ ...item, index: fieldIndex });
                      }}
                    >
                      <Pencil className="tw-h-3 tw-w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="tw-text-xs">Edit</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="ghost"
                size="icon"
                className="tw-h-7 tw-w-7 tw-text-destructive hover:tw-text-destructive"
                onClick={() => handleDeleteHeatSource(fieldIndex)}
              >
                <Trash2 className="tw-h-3 tw-w-3" />
              </Button>
            </>
          )}
        </div>
      );
    }

    // Edit mode - Use same heatForm with Form wrapper (matches original pattern)
    return (
      <Form {...heatForm}>
        <form
          key={itemId}
          onSubmit={handleHeatFormSubmit}
          className="tw-border tw-border-primary/30 tw-rounded-md tw-p-2 tw-bg-primary/5 tw-space-y-2"
        >
          <div className="tw-flex tw-items-start tw-gap-2">
            <div className="tw-flex-[2]">
              <FormSelect
                control={heatForm.control}
                name="HeatSource"
                placeholder="Select heat source"
                options={availableHeatSourceType}
                disabled={lockStatus}
                required
              />
            </div>
            <div className="tw-flex-[2]">
              <FormUnitInput
                control={heatForm.control}
                name="HeatAmount"
                unitOptions={ENERGY_UNIT_TYPES_OPTIONs}
                unitName="heatUnit"
                placeholder="0.00"
                enableLimitedDecimal
                viewDecimalNumber={12}
                disabled={lockStatus}
                required="Heat amount is required"
                validate={{
                  valueCheck: (v: string | number) =>
                    valueGraterThanZero(v) || "Heat Amount must be greater than 0",
                  maxCheck: (v: string | number) => validateHeatSourceAmount(v)
                }}
              />
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="submit"
                    variant="default"
                    size="icon"
                    className="tw-h-[42px] tw-w-[42px]"
                    disabled={lockStatus}
                  >
                    <Check className="tw-h-3.5 tw-w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="tw-text-xs">Update changes</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </form>
      </Form>
    );
  };

  const renderPagination = (): JSX.Element | null => {
    if (heatPages <= 1) return null;
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-pt-2">
        <Button
          variant="ghost"
          size="icon"
          className="tw-h-7 tw-w-7"
          disabled={heatPage <= 1}
          onClick={() => setHeatPage(heatPage - 1)}
        >
          <ChevronLeft className="tw-h-3.5 tw-w-3.5" />
        </Button>
        <span className="tw-text-[10px] tw-text-muted-foreground tw-font-mono">
          {heatPage} / {heatPages}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="tw-h-7 tw-w-7"
          disabled={heatPage >= heatPages}
          onClick={() => setHeatPage(heatPage + 1)}
        >
          <ChevronRight className="tw-h-3.5 tw-w-3.5" />
        </Button>
      </div>
    );
  };

  return (
    <Collapsible open={heatOpen} onOpenChange={setHeatOpen}>
      <div className="tw-border tw-border-dashed tw-border-accent/50 tw-rounded-md">
        <CollapsibleTrigger className="tw-flex tw-items-center tw-justify-between tw-w-full tw-p-1 hover:tw-bg-accent/5 tw-transition-colors tw-rounded-md">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Flame className="tw-h-3.5 tw-w-3.5 tw-text-accent" />
            <span className="tw-text-xs tw-font-medium tw-text-foreground">Heat Sources</span>
            {heatSource.length > 0 && (
              <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
                {heatSource.length}
              </Badge>
            )}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-[10px] tw-font-mono tw-text-muted-foreground">
              Total: {totalHeat.toFixed(2)} MJ/kg
            </span>
            {heatOpen ? (
              <ChevronUp className="tw-h-3 tw-w-3 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-3 tw-w-3 tw-text-muted-foreground" />
            )}
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="tw-px-3 tw-pb-3 tw-space-y-2">
          {paginatedHeat.map((item: any, paginatedIndex: number) => {
            const actualIndex = (heatPage - 1) * PAGINATION_ITEMS_PER_PAGE + paginatedIndex;
            return renderHeatRow(item, actualIndex, false);
          })}
          {renderPagination()}
          {heatSource.length > 0 && <div className="tw-border-t tw-border-dashed tw-border-border" />}
          <div>
            <p className="tw-text-[10px] tw-text-muted-foreground tw-mb-1">Add new heat source</p>
            {renderHeatRow(null as any, 0, true)}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default ProcessHeatSource;
